import { deleteCookie, getCookie } from 'cookies-next/client';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

export const POST_LOGOUT_REDIRECT_COOKIE = 'heidi-post-logout-redirect';

/**
 * Trusted domains for redirect validation.
 * Only URLs from these origins are allowed for absolute URL redirects.
 */
const TRUSTED_DOMAINS = [
  'heidihealth.com',
  'secure.heidihealth.com',
  'widget.heidihealth.com',
  'localhost',
];

/**
 * Validates a redirect URL to prevent open redirect vulnerabilities.
 * Accepts:
 * - Relative paths starting with "/" (but not "//" or containing "\")
 * - Absolute URLs whose origin matches a trusted domain
 * Returns a safe URL or "/" as fallback.
 */
export const validateRedirectUrl = (url: string): string => {
  if (!url || typeof url !== 'string' || url.length === 0) {
    return '/';
  }

  // Check for relative path (must start with "/" but not "//")
  if (url.startsWith('/')) {
    // Reject protocol-relative URLs ("//example.com") and backslash attempts
    if (url.startsWith('//') || url.includes('\\')) {
      return '/';
    }
    // Valid relative path
    return url;
  }

  // Check for absolute URL
  try {
    const parsedUrl = new URL(url);
    const hostname = parsedUrl.hostname.toLowerCase();

    // Check if hostname matches trusted domains or is a subdomain
    const isTrusted = TRUSTED_DOMAINS.some((domain) => {
      return hostname === domain || hostname.endsWith(`.${domain}`);
    });

    if (isTrusted) {
      return url;
    }

    // Untrusted absolute URL
    return '/';
  } catch {
    // Invalid URL format
    return '/';
  }
};

export function usePostLogoutRedirect() {
  const router = useRouter();

  useEffect(() => {
    if (router.pathname !== '/') {
      return;
    }

    const redirectUrl = getCookie(POST_LOGOUT_REDIRECT_COOKIE);

    if (redirectUrl && typeof redirectUrl === 'string' && redirectUrl.length > 0) {
      // Validate redirect URL before using it
      const validatedUrl = validateRedirectUrl(redirectUrl);

      // Only delete cookie and redirect if validation succeeded
      deleteCookie(POST_LOGOUT_REDIRECT_COOKIE);
      void router.replace(validatedUrl);
    }
  }, [router]);
}
